import { useCallback, useMemo, useState } from 'react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { getDemoModuleRows } from './demoModuleData';
import { getJobPhoneModule } from './moduleCatalog';
import { saveErrorMessage } from './saveQueue';

export const useModuleSession = ({ instance, accounts, demo = false }) => {
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState('');
  const [saveState, setSaveState] = useState({ status: 'idle', message: '' });

  const loadRows = useCallback(async (projectId, moduleId) => {
    const module = getJobPhoneModule(moduleId);
    if (!module) {
      setRows([]);
      setError('This phone module is not available.');
      return;
    }

    setLoadingRows(true);
    setError('');
    try {
      if (demo) {
        setRows(getDemoModuleRows(projectId, moduleId));
        return;
      }
      const endpoint = module.api.getData.replace('{projectId}', projectId);
      const response = await authenticatedApiCall(instance, accounts, 'GET', endpoint);
      if (response.data?.success === false) {
        throw new Error(response.data.message || `Could not load ${module.label}`);
      }
      setRows(module.transform(response.data?.data ?? response.data));
    } catch (err) {
      setRows([]);
      setError(saveErrorMessage(err, `Could not load ${module.label}`));
    } finally {
      setLoadingRows(false);
    }
  }, [accounts, demo, instance]);

  const saveField = useCallback(async (projectId, moduleId, row, fieldKey, newValue) => {
    const module = getJobPhoneModule(moduleId);
    if (!module || !row) return false;
    const nextRow = { ...row, [fieldKey]: newValue };

    if (demo) {
      setRows((current) => current.map((item) => (
        String(item.id) === String(row.id) ? nextRow : item
      )));
      setSaveState({ status: 'saved', message: 'Saved on this phone (preview)' });
      return true;
    }

    setSaveState({ status: 'saving', message: 'Saving…' });
    try {
      if (module.api.saveChanges) {
        const change = module.buildChangePayload(row.id, fieldKey, row[fieldKey], newValue);
        if (!change) return true;
        await authenticatedApiCall(instance, accounts, 'POST', module.api.saveChanges, {
          project_id: projectId,
          changes: [change],
        });
      } else {
        await authenticatedApiCall(
          instance,
          accounts,
          'PUT',
          module.api.save.replace('{id}', row.id),
          nextRow
        );
      }
      setRows((current) => current.map((item) => (
        String(item.id) === String(row.id) ? nextRow : item
      )));
      setSaveState({ status: 'saved', message: 'Saved' });
      return true;
    } catch (err) {
      setSaveState({
        status: 'error',
        message: saveErrorMessage(err, 'Could not save — check your connection and try again'),
      });
      return false;
    }
  }, [accounts, demo, instance]);

  return useMemo(() => ({
    rows,
    loadingRows,
    error,
    saveState,
    loadRows,
    saveField,
  }), [error, loadRows, loadingRows, rows, saveField, saveState]);
};

